// ---------------------------------------------------------------------------
// API Client — all HTTP communication with LifeRaft and HART backends
// ---------------------------------------------------------------------------

import { CONFIG, LIFERAFT_LOOKBACK_DAYS } from './config';
import type { HartEventsResponse, ClusterPostsResponse, DailyVolume } from './types';
import { showLoading, hideLoading, updateConnectionStatus, handleAPIError } from './ui';

export class APIClient {
  baseUrl: string;
  currentSource: string;
  axios: AxiosInstance;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.currentSource = 'liferaft';
    const sameOriginApi = typeof window !== 'undefined' && baseUrl.startsWith('/api');
    this.axios = axios.create({
      baseURL: baseUrl,
      timeout: 120_000,
      headers: { 'Content-Type': 'application/json' },
      // Only send cookies when calling a remote API (production); proxied local /api does not need them
      withCredentials: !sameOriginApi,
    } as Record<string, unknown>);

    this.axios.interceptors.request.use(
      (cfg: AxiosRequestConfig) => {
        showLoading(`Making request to ${cfg.url}...`);
        if (this.currentSource === 'hart') {
          const token = CONFIG.apiSources.hart.token;
          if (token) cfg.headers['Authorization'] = `Bearer ${token}`;
        }
        return cfg;
      },
      (err: unknown) => { hideLoading(); return Promise.reject(err); },
    );

    this.axios.interceptors.response.use(
      (res: AxiosResponse) => { hideLoading(); updateConnectionStatus(true); return res; },
      (err: unknown) => { hideLoading(); updateConnectionStatus(false); handleAPIError(err as AxiosError); return Promise.reject(err); },
    );
  }

  switchSource(source: string): void {
    this.currentSource = source;
    const cfg = CONFIG.apiSources[source];
    this.baseUrl = cfg.baseUrl;
    this.axios.defaults.baseURL = cfg.baseUrl;
    if (source === 'hart' && cfg.token) {
      this.axios.defaults.headers['Authorization'] = `Bearer ${cfg.token}`;
    } else {
      delete this.axios.defaults.headers['Authorization'];
    }
  }

  // ── HART endpoints ──────────────────────────────────────────────────────

  async getHartEvents(page = 0, pageCount = 10): Promise<HartEventsResponse> {
    const r = await this.axios.get('/hart/events', { params: { page, pageCount }, timeout: 120_000 } as Record<string, unknown>);
    return r.data as HartEventsResponse;
  }

  // ── LifeRaft endpoints ─────────────────────────────────────────────────

  async healthCheck(): Promise<unknown> {
    const r = await this.axios.get('/health');
    return r.data;
  }

  async getTodayTrends(): Promise<{ trends: unknown[]; total_trends: number }> {
    return (await this.axios.get('/trends/today')).data as { trends: unknown[]; total_trends: number };
  }

  async getTrendsByDate(date: string): Promise<{ trends: unknown[]; total_trends: number }> {
    return (await this.axios.get(`/trends/${date}`)).data as { trends: unknown[]; total_trends: number };
  }

  async getTrendReport(date: string): Promise<unknown> {
    return (await this.axios.get(`/trends/${date}/report`)).data;
  }

  async detectTrends(date: string, detectTopicsFirst = true): Promise<unknown> {
    return (await this.axios.post(`/trends/${date}/detect`, null, { params: { detect_topics_first: detectTopicsFirst } })).data;
  }

  async getTopicsByDate(date: string): Promise<{ topics: unknown[]; total_topics: number }> {
    return (await this.axios.get(`/topics/${date}`)).data as { topics: unknown[]; total_topics: number };
  }

  async getTopicsByRange(
    days = LIFERAFT_LOOKBACK_DAYS,
    endOffsetDays = 0,
    method = 'auto',
  ): Promise<unknown> {
    return (await this.axios.get(`/topics/range/${days}`, {
      params: { method, end_offset_days: endOffsetDays },
    })).data;
  }

  async getLiferaftDashboard(refresh = false): Promise<{
    lookback_days: number;
    compare_days: number;
    current: { topics?: unknown[]; total_clusters_merged?: number };
    previous: { topics?: unknown[]; total_clusters_merged?: number };
  }> {
    return (await this.axios.get('/topics/liferaft-dashboard', {
      params: { refresh },
    })).data as {
      lookback_days: number;
      compare_days: number;
      current: { topics?: unknown[]; total_clusters_merged?: number; daily_volumes?: DailyVolume[] };
      previous: { topics?: unknown[]; total_clusters_merged?: number };
    };
  }

  async detectTopics(date: string, method = 'auto'): Promise<unknown> {
    return (await this.axios.post(`/topics/${date}/detect`, null, { params: { method } })).data;
  }

  async runFullAnalysis(startDate: string, endDate: string, detectTopics = true, detectTrends = true): Promise<unknown> {
    return (await this.axios.post('/analysis/run', { start_date: startDate, end_date: endDate, detect_topics: detectTopics, detect_trends: detectTrends })).data;
  }

  async getTrendEvolution(startDate: string, endDate: string): Promise<unknown> {
    return (await this.axios.get('/analysis/evolution', { params: { start_date: startDate, end_date: endDate } })).data;
  }

  async ingestData(data: unknown, sourceType: string, batchSize = 100): Promise<unknown> {
    return (await this.axios.post('/data/ingest', { data, source_type: sourceType, batch_size: batchSize })).data;
  }

  async getClusterPosts(clusterId: string, limit = 100): Promise<ClusterPostsResponse> {
    return (await this.axios.get(`/topics/cluster/${clusterId}/posts`, {
      params: { limit },
    })).data as ClusterPostsResponse;
  }
}

/** Singleton API client instance */
export const api = new APIClient(CONFIG.apiBaseUrl);

